import { useState, useRef, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { TAREFAS_CONFIG_DESIGN as D } from './TarefasConfig.design';
import ModalWorkflow from './ModalWorkflow';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import {
  actionCriarWorkflow,
  actionEditarWorkflow,
  actionDeletarWorkflow,
  actionReordenarWorkflows,
} from 'src/actions/tarefas/actionTarefasConfig';
import Iconify from 'src/components/Iconify';

export default function WorkflowsTab({
  listaWorkflows,
  onRefresh,
  clienteId,
  openCreate,
  onOpenCreateHandled,
}) {
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [erroDelete, setErroDelete] = useState('');

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (!openCreate) return;
    setOpenModal(true);
    setAction('Criar');
    setSelectedWorkflow(null);
    onOpenCreateHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCreate]);

  function resetForm() {
    setSelectedWorkflow(null);
    setAction('');
  }

  function openEdit(row) {
    setOpenModal(true);
    setAction('Editar');
    setSelectedWorkflow(row);
  }

  async function handleCriar(data) {
    try {
      setLoading(true);
      await actionCriarWorkflow({ ...data, cliente_id: clienteId });
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditar(data) {
    try {
      setLoading(true);
      await actionEditarWorkflow(selectedWorkflow._id, data);
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeletar() {
    try {
      setLoading(true);
      setErroDelete('');
      await actionDeletarWorkflow(selectedWorkflow._id);
      await onRefresh();
      setOpenConfirmacao(false);
      resetForm();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Erro ao deletar workflow.';
      setErroDelete(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleDragEnd() {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...listaWorkflows];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    dragItem.current = null;
    dragOverItem.current = null;

    try {
      await actionReordenarWorkflows({ ids: reordered.map(w => w._id) });
      await onRefresh();
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className={D.stack}>
      <ModalConfimacao
        open={openConfirmacao}
        onClose={() => { setOpenConfirmacao(false); setErroDelete(''); }}
        onConfirm={handleDeletar}
        loadingFinalizar={loading}
        mensagem="Deseja mesmo excluir esse workflow?"
        erro={erroDelete}
      />
      <ModalWorkflow
        open={openModal}
        action={action}
        workflow={selectedWorkflow}
        onSubmit={action === 'Criar' ? handleCriar : handleEditar}
        onClose={() => { setOpenModal(false); resetForm(); }}
      />

      <div className={D.sectionHeader}>
        <div className={D.sectionHeaderMain}>
          <h2 className={D.sectionHeaderTitle}>Workflows</h2>
          <p className={D.sectionHeaderSub}>
            Cada workflow é um caminho de estágios. Arraste para mudar a ordem na lista de tarefas.
          </p>
        </div>
      </div>

      <div className={D.cardFlush}>
        {listaWorkflows.length === 0 ? (
          <div className={D.emptyState}>
            <div className={D.emptyIcon}>
              <Iconify icon="eva:checkmark-square-2-outline" width={28} />
            </div>
            <p className={D.emptyTitle}>Nenhum workflow encontrado</p>
            <p className={D.emptyDesc}>
              O workflow padrão é criado automaticamente. Você também pode criar um fluxo próprio.
            </p>
            <button
              type="button"
              onClick={() => { setOpenModal(true); setAction('Criar'); setSelectedWorkflow(null); }}
              className={`${D.link} mt-4`}
            >
              Criar workflow
            </button>
          </div>
        ) : (
          <div className={D.tableWrap}>
            <div className={D.tableHeadRow}>
              <div className={`w-10 ${D.tableHead}`} />
              <div className={`flex-1 min-w-[200px] pr-4 ${D.tableHead}`}>Nome</div>
              <div className={`w-48 ${D.tableHead}`}>Estágios</div>
              <div className={`w-32 text-center ${D.tableHead}`}>Tipo</div>
              <div className={`w-24 text-right ${D.tableHead}`}>Ações</div>
            </div>

            {listaWorkflows.map((row, index) => (
              <div
                key={row._id}
                className={D.tableRow}
                draggable
                onDragStart={() => { dragItem.current = index; }}
                onDragEnter={() => { dragOverItem.current = index; }}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="w-10 flex items-center text-[#d4d4d8]">
                  <Iconify icon="eva:menu-outline" width={16} />
                </div>

                <div className="flex-1 min-w-[200px] pr-4">
                  <p className={`${D.tableCell} font-semibold truncate`}>{row.nome}</p>
                  {row.descricao && (
                    <p className={`${D.tableCellMuted} truncate mt-0.5`}>{row.descricao}</p>
                  )}
                </div>

                <div className="w-48">
                  <div className="flex items-center gap-1 flex-wrap">
                    {(row.estagios || []).sort((a, b) => a.ordem - b.ordem).map((e, i) => (
                      <Tooltip key={i} title={e.nome}>
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-white"
                          style={{ backgroundColor: e.cor }}
                        />
                      </Tooltip>
                    ))}
                    <span className={`${D.tableCellMuted} ml-1`}>
                      {(row.estagios || []).length} estágios
                    </span>
                  </div>
                </div>

                <div className="w-32 flex justify-center">
                  {row.tipo === 'padrao' ? (
                    <span className={`${D.badge} ${D.badgeNeutral}`}>Padrão</span>
                  ) : (
                    <span className={`${D.badge} ${D.badgeSoft}`}>Custom</span>
                  )}
                </div>

                <div className="w-24 flex justify-end gap-1.5">
                  <button
                    type="button"
                    title="Editar"
                    onClick={() => openEdit(row)}
                    className={D.iconButton}
                  >
                    <Iconify icon="eva:edit-2-outline" width={16} />
                  </button>
                  <span>
                    <button
                      type="button"
                      title={row.tipo === 'padrao' ? 'Workflow padrão não pode ser excluído' : 'Excluir'}
                      onClick={() => {
                        if (row.tipo === 'padrao') return;
                        setSelectedWorkflow(row);
                        setOpenConfirmacao(true);
                      }}
                      disabled={row.tipo === 'padrao'}
                      className={row.tipo === 'padrao' ? D.iconButtonDisabled : D.iconButtonDanger}
                    >
                      <Iconify icon="eva:trash-2-outline" width={16} />
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
